import { config } from "@nova/configuration/browser"

const TILE_URL = "https://api.maptiler.com/tiles/terrain-rgb-v2/{z}/{x}/{y}.webp?key={MAPTILER_KEY}"
const ZOOM_LEVEL = 14 // Available levels are 0-10
const CACHE_TIMEOUT = 10_000 // 10 seconds

// TerrainCache
//   Usage:
//     const terrain = new TerrainCache(MAPTILER_KEY);
//     const elev = terrain.elevation(-120, 50) ?? fallback;
//
//   Behaviour:
//      - elevation calls are fast as only the cache is checked
//      - if the terrain is missing, elevation returns undefined
//      - missing terrain will be fetched in the background
//      - cache is cleared after 10 seconds of no use

export class TerrainCache {
    constructor() {
        this.cache = new Map()
        this.requests = new Map()
        this.resetCleanup()
    }

    elevation(lon, lat, callback, overwriteCallback) {
        this.callback = callback
        // putting here to avoid ssr
        if (!this.key) this.key = config.get("mapTilerApiKey")
        // Inputs
        //   lon in degrees between -180 and +180
        //   lat in degrees between -90 and +90
        // Output
        //   height in meters if terrain is in cache
        //   undefined if the terrain is not in cache

        this.resetCleanup()

        const { floor } = Math
        const index = tileIndexClamped(lon, lat, ZOOM_LEVEL)
        const x = floor(index[0])
        const y = floor(index[1])
        const key = JSON.stringify([x, y]) // Gotta love JS

        if (this.cache.has(key)) {
            if (callback && (!overwriteCallback || this.callback === callback)) {
                callback(this.parseTile(index, key))
                callback = undefined
            }
            return this.parseTile(index, key)
        } else if (!this.requests.get(key)) {
            this.requests.set(
                key,
                new Promise((res) => {
                    const url = TILE_URL.replace("{MAPTILER_KEY}", this.key)
                        .replace("{x}", x)
                        .replace("{y}", y)
                        .replace("{z}", ZOOM_LEVEL)
                    const img = new Image()
                    img.crossOrigin = "anonymous"
                    img.onload = () => {
                        const canvas = document.createElement("canvas")
                        canvas.width = img.width
                        canvas.height = img.height
                        const context = canvas.getContext("2d")
                        context.drawImage(img, 0, 0)
                        const imageData = context.getImageData(0, 0, img.width, img.height)
                        this.cache.set(key, imageData)
                        this.requests.delete(key)
                        res()
                        if (callback && (!overwriteCallback || this.callback === callback)) {
                            callback(this.parseTile(index, key))
                        }
                    }
                    // img.onerror = console.error;
                    img.src = url
                })
            )
        } else {
            this.requests.get(key).then(() => {
                if (callback && (!overwriteCallback || this.callback === callback)) {
                    callback(this.parseTile(index, key))
                }
            })
        }

        return undefined
    }

    parseTile(index, key) {
        const { floor } = Math
        const imageData = this.cache.get(key)
        const u = index[0] % 1
        const v = index[1] % 1
        const w = floor(u * imageData.width)
        const h = floor(v * imageData.height)
        const offset = (h * imageData.width + w) * 4
        const red = imageData.data[offset + 0]
        const green = imageData.data[offset + 1]
        const blue = imageData.data[offset + 2]
        const height = -10000 + (red * 256 * 256 + green * 256 + blue) * 0.1
        return height
    }

    resetCleanup() {
        if (this.cleanup) {
            clearTimeout(this.cleanup)
        }
        this.cleanup = setTimeout(() => {
            this.cache.clear()
            this.requests.clear()
            this.cleanup = undefined
        }, CACHE_TIMEOUT)
    }
}

function tileIndexClamped(lon, lat, zoom) {
    const { pow, log, tan, cos, PI, min, max } = Math
    const n = pow(2, zoom)
    const x = (n * (lon + 180)) / 360
    const latRad = (lat * PI) / 180
    const k = log(tan(latRad) + 1 / cos(latRad)) / PI
    const kClamped = min(max(k, -1), 1)
    const y = (n * (1 - kClamped)) / 2
    return [x, y]
}
